import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguageStore } from "@/stores/language";
import { useUIStore } from "@/stores/ui";
import { Menu, X } from "lucide-react";
import { CartDrawer } from "@/components/cart";
import { useEffect, useRef, useCallback, useMemo } from "react";

export const Navigation = () => {
  const { language, setLanguage, t } = useLanguageStore();
  const location = useLocation();
  const { isMobileMenuOpen, toggleMobileMenu, setMobileMenuOpen } = useUIStore();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const navLinks = useMemo(() => [
    { to: "/shop", label: t("Shop", "Tienda") },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: t("Contact", "Contacto") },
  ], [t]);

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isMobileMenuOpen) {
      setMobileMenuOpen(false);
      menuButtonRef.current?.focus();
    }
  }, [isMobileMenuOpen, setMobileMenuOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (isMobileMenuOpen && mobileMenuRef.current) {
      const firstLink = mobileMenuRef.current.querySelector('a');
      firstLink?.focus();
    }
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-foreground/5">
        <nav 
          className="container mx-auto px-6 py-4"
          role="navigation"
          aria-label={t("Main navigation", "Navegación principal")}
        >
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              onClick={handleNavClick}
              aria-label={t("FSHN AI - Go to homepage", "FSHN AI - Ir a la página principal")}
              className="touch-action-manipulation"
            >
              <div className="flex flex-col">
                <span className="text-xl md:text-2xl font-display font-bold tracking-tight leading-none transition-opacity hover:opacity-70">
                  FSHN AI
                </span>
                <span className="text-[7px] md:text-[10px] font-mono tracking-[0.2em] md:tracking-[0.25em] text-muted-foreground uppercase leading-none mt-0.5">
                  Prompt Engineering for Fashion
                </span>
              </div>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link 
                  key={link.to} 
                  to={link.to}
                  role="menuitem"
                  aria-current={location.pathname === link.to ? "page" : undefined}
                  className={`text-sm font-body hover:text-foreground transition-colors touch-action-manipulation ${
                    location.pathname === link.to ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLanguage(language === "en" ? "es" : "en")}
                className="text-sm font-mono text-muted-foreground hover:text-foreground touch-action-manipulation min-h-[44px] min-w-[44px]"
                aria-label={language === "en" ? "ES - Switch to Spanish" : "EN - Cambiar a inglés"}
              >
                {language === "en" ? "ES" : "EN"}
              </Button>

              <CartDrawer />
            </div>

            {/* Mobile Navigation Controls */}
            <div className="flex md:hidden items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLanguage(language === "en" ? "es" : "en")}
                className="text-sm font-mono text-muted-foreground touch-action-manipulation min-h-[44px] min-w-[44px]"
                aria-label={language === "en" ? "ES - Switch to Spanish" : "EN - Cambiar a inglés"}
              >
                {language === "en" ? "ES" : "EN"}
              </Button>

              <CartDrawer />

              <Button
                ref={menuButtonRef}
                variant="ghost"
                size="icon"
                onClick={() => toggleMobileMenu()}
                aria-label={isMobileMenuOpen 
                  ? t("Close navigation menu", "Cerrar menú de navegación")
                  : t("Open navigation menu", "Abrir menú de navegación")}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
                className="touch-action-manipulation min-h-[44px] min-w-[44px]"
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Menu className="h-5 w-5" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Full-screen Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          id="mobile-menu"
          className="fixed inset-0 z-40 bg-background md:hidden css-fade-in"
          role="menu"
          aria-label={t("Mobile navigation", "Navegación móvil")}
        >
          <div className="flex flex-col justify-center items-center h-full gap-8">
            {navLinks.map((link, index) => (
              <div
                key={link.to}
                className="css-fade-in-up"
                style={{ '--animation-delay': `${index * 100}ms` } as React.CSSProperties}
              >
                <Link 
                  to={link.to}
                  onClick={handleNavClick}
                  role="menuitem"
                  aria-current={location.pathname === link.to ? "page" : undefined}
                  className={`text-4xl md:text-5xl font-bold tracking-tight transition-colors touch-action-manipulation ${
                    location.pathname === link.to ? 'text-accent' : 'text-foreground hover:text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
